import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Container from '@/components/ui/container'
import Icon from '@/components/ui/icon'

const icons = ['hub', 'sync', 'engineering']
const highlightedIndex = 1

export default function ValueProposition() {
  const { t } = useTranslation()
  const sectionRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  const items = t('valueProposition.items', { returnObjects: true }) as {
    title: string
    description: string
  }[]

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <section ref={sectionRef} className="py-16 md:py-24 lg:py-32 bg-surface">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-outline-variant/10">
          {items.map((item, i) => (
            <div
              key={i}
              className={`group p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-between min-h-0 md:min-h-[400px] transition-all duration-500 ease-out
                ${i < items.length - 1 ? 'md:border-r border-outline-variant/10' : ''}
                ${i === highlightedIndex ? 'bg-surface-container-low' : ''}
                ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                hover:bg-surface-container-low hover:shadow-lg hover:shadow-primary/5`}
              style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}
            >
              <div>
                <Icon
                  name={icons[i]}
                  className="text-primary text-4xl mb-8 block transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1"
                />
                <h3 className="font-display text-2xl font-bold tracking-tight mb-4 transition-colors duration-300 group-hover:text-primary">
                  {item.title}
                </h3>
                <p className="text-on-surface-variant leading-relaxed font-light whitespace-pre-line">
                  {item.description}
                </p>
              </div>
              <div className="h-1 w-12 bg-primary transition-all duration-300 group-hover:w-24" />
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}
